import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    Divider,
    CircularProgress,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { getTimeoffRequestById, TimeoffRequest } from '../dataProvider';

export interface TimeoffRequestDetailDialogProps {
    open: boolean;
    requestId: number | null;
    onClose: () => void;
}

const getStatusColor = (status: string) => {
    switch (status) {
        case 'Approved': return 'success';
        case 'Rejected': return 'error';
        case 'Cancelled': return 'default';
        case 'Pending': return 'warning';
        default: return 'default';
    }
};

const formatPartialDay = (partial: string) => {
    switch (partial) {
        case 'FirstHalf': return 'First Half';
        case 'SecondHalf': return 'Second Half';
        default: return 'Full';
    }
};

const formatDate = (dateStr: string | undefined | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString(undefined, { timeZone: 'UTC' });
};

const formatDateOnly = (dateStr: string | undefined | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(undefined, { timeZone: 'UTC' });
};

/** A single labelled row in the details section. */
const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <Box display="flex" gap={1} mb={0.5} alignItems="center">
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
            {label}
        </Typography>
        <Typography variant="body2" component="div">{value}</Typography>
    </Box>
);

/** Minimal vertical timeline rendered without @mui/lab. */
const TimelineSection: React.FC<{ request: TimeoffRequest }> = ({ request }) => {
    const events: Array<{ label: string; timestamp: string | undefined | null; detail?: string }> = [];

    events.push({
        label: 'Created',
        timestamp: request.created_date,
    });

    if (request.status === 'Approved' && request.reviewed_date) {
        events.push({
            label: 'Approved',
            timestamp: request.reviewed_date,
            detail: request.review_comment
                ? `Comment: ${request.review_comment}`
                : undefined,
        });
    }

    if (request.status === 'Rejected' && request.reviewed_date) {
        events.push({
            label: 'Rejected',
            timestamp: request.reviewed_date,
            detail: request.review_comment
                ? `Comment: ${request.review_comment}`
                : undefined,
        });
    }

    if (request.status === 'Cancelled') {
        events.push({
            label: 'Cancelled',
            timestamp: undefined,
        });
    }

    return (
        <Box>
            <Typography variant="subtitle2" gutterBottom>
                Timeline
            </Typography>
            {events.map((evt, idx) => (
                <Box key={idx} display="flex" alignItems="flex-start" gap={1.5} mb={1}>
                    <Box
                        sx={{
                            mt: '4px',
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            flexShrink: 0,
                        }}
                    />
                    <Box>
                        <Typography variant="body2" fontWeight="bold">
                            {evt.label}
                        </Typography>
                        {evt.timestamp && (
                            <Typography variant="caption" color="text.secondary">
                                {formatDate(evt.timestamp)}
                            </Typography>
                        )}
                        {evt.detail && (
                            <Typography variant="body2" color="text.secondary">
                                {evt.detail}
                            </Typography>
                        )}
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

/** Dialog showing full details and a lifecycle timeline for a time-off request. */
export const TimeoffRequestDetailDialog: React.FC<TimeoffRequestDetailDialogProps> = ({
    open,
    requestId,
    onClose,
}) => {
    const notify = useNotify();
    const [request, setRequest] = useState<TimeoffRequest | null>(null);
    const [loading, setLoading] = useState(false);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        if (!open || requestId === null) return;

        let cancelled = false;
        setLoading(true);
        setRequest(null);
        setNotFound(false);

        getTimeoffRequestById(requestId)
            .then((data) => {
                if (!cancelled) {
                    setRequest(data);
                }
            })
            .catch((error: any) => {
                if (cancelled) return;
                if (error?.status === 404) {
                    setNotFound(true);
                } else {
                    const msg = error?.body?.message || error?.message || 'Failed to load request';
                    notify(msg, { type: 'error' });
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, requestId]);

    const handleClose = () => {
        setRequest(null);
        setNotFound(false);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Time-Off Request Details</DialogTitle>
            <DialogContent>
                {loading && (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && notFound && (
                    <Typography color="error">Request not found.</Typography>
                )}

                {!loading && !notFound && request && (
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Employee
                            </Typography>
                            <DetailRow label="Name" value={request.full_name ?? '—'} />
                            <DetailRow label="Email" value={request.email ?? '—'} />
                        </Box>

                        <Divider />

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Request
                            </Typography>
                            <DetailRow label="Leave Type" value={request.leave_type} />
                            <DetailRow
                                label="Start Date"
                                value={`${formatDateOnly(request.start_date)} (${formatPartialDay(request.start_partial)})`}
                            />
                            <DetailRow
                                label="End Date"
                                value={`${formatDateOnly(request.end_date)} (${formatPartialDay(request.end_partial)})`}
                            />
                            <DetailRow label="Work Days" value={request.work_days} />
                            <DetailRow
                                label="Status"
                                value={
                                    <Chip
                                        label={request.status}
                                        color={getStatusColor(request.status) as any}
                                        size="small"
                                    />
                                }
                            />
                            {request.note && (
                                <DetailRow label="Note" value={request.note} />
                            )}
                            {request.review_comment && (
                                <DetailRow label="Review Comment" value={request.review_comment} />
                            )}
                        </Box>

                        <Divider />

                        <TimelineSection request={request} />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};
